import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { MessageSquare, CornerDownRight } from 'lucide-react';
import type { ReviewCommentWithReplies } from './ReviewCommentsCallout';

export interface ReviewCommentsModalProps {
  open: boolean;
  onClose: () => void;
  targetType?: string;
  targetLabel: string;
  targetSubtitle?: string;
  comments: ReviewCommentWithReplies[];
}

export interface ReviewCommentsIndicatorProps {
  count: number;
  onClick: () => void;
  triggerProps?: Record<string, unknown>;
}

export function ReviewCommentsIndicator({
  count,
  onClick,
  triggerProps,
}: ReviewCommentsIndicatorProps) {
  if (count <= 0) return null;
  return (
    <button
      type="button"
      aria-label={`View ${count} review ${count === 1 ? 'comment' : 'comments'}`}
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-500/20 dark:text-amber-300 transition-colors"
      {...triggerProps}
    >
      <MessageSquare className="h-3 w-3" />
      {count}
    </button>
  );
}

export default function ReviewCommentsModal({
  open,
  onClose,
  targetType,
  targetLabel,
  targetSubtitle,
  comments,
}: ReviewCommentsModalProps) {
  const openComments = comments.filter(
    (c) => c.status === 'suggested' && c.target_type !== 'comment',
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <span>
              Review Comments — {targetType ? `${targetType}: ` : ''}
              {targetLabel}
            </span>
          </DialogTitle>
          {targetSubtitle && <DialogDescription>{targetSubtitle}</DialogDescription>}
        </DialogHeader>

        <div className="space-y-3 py-2">
          {openComments.map((c) => (
            <div
              key={c.id}
              className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm space-y-1.5"
            >
              <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{c.created_by || c.source}</span>
                {c.created_by && c.source && (
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {c.source}
                  </span>
                )}
                {c.severity && (
                  <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[11px] font-medium text-amber-900 dark:text-amber-200">
                    {c.severity}
                  </span>
                )}
                {c.quality_check_type && (
                  <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[11px] font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                    {c.quality_check_type}
                  </span>
                )}
                {c.created_at && (
                  <span className="text-[11px] text-muted-foreground">{c.created_at}</span>
                )}
              </div>
              {c.description && <p className="text-sm whitespace-pre-wrap">{c.description}</p>}
              {c.replies && c.replies.length > 0 && (
                <div className="mt-1.5 space-y-1.5 border-l-2 border-amber-500/30 pl-3">
                  {c.replies.map((r) => (
                    <div key={r.id} className="space-y-0.5 text-xs">
                      <div className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
                        <CornerDownRight className="h-3 w-3 shrink-0" />
                        <span className="font-medium text-foreground">
                          {r.created_by || r.source}
                        </span>
                        {r.created_at && (
                          <span className="text-[11px] text-muted-foreground">{r.created_at}</span>
                        )}
                      </div>
                      {r.description && (
                        <p className="pl-4 text-xs whitespace-pre-wrap text-foreground">
                          {r.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
